/* eslint-disable no-console -- This module is the console abstraction; direct console calls are intentional. */
export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error';
export type LogCategory =
  | 'boot' | 'api' | 'query' | 'mutation' | 'auth' | 'permission'
  | 'nav' | 'ui' | 'lifecycle' | 'perf' | 'error' | 'i18n' | 'form';

export type LogVerbosity = 'silent' | 'normal' | 'tester' | 'dev';

export interface LogEntry {
  ts: string;
  relMs: number;
  level: LogLevel;
  category: LogCategory;
  message: string;
  data?: Record<string, unknown>;
  correlationId?: string;
}

const LEVEL_RANK: Record<LogLevel, number> = { trace: 0, debug: 1, info: 2, warn: 3, error: 4 };

const VERBOSITY_THRESHOLD: Record<LogVerbosity, number> = {
  silent: 99,
  normal: LEVEL_RANK.warn,
  tester: LEVEL_RANK.info,
  dev: LEVEL_RANK.trace,
};

const CATEGORY_STYLE: Record<LogCategory, { color: string; emoji: string }> = {
  boot:       { color: '#7c3aed', emoji: '🚀' },
  api:        { color: '#2563eb', emoji: '🔵' },
  query:      { color: '#0891b2', emoji: '🔄' },
  mutation:   { color: '#0d9488', emoji: '⚡' },
  auth:       { color: '#9333ea', emoji: '🔐' },
  permission: { color: '#c026d3', emoji: '🛡️' },
  nav:        { color: '#0ea5e9', emoji: '📍' },
  ui:         { color: '#475569', emoji: '🖱️' },
  lifecycle:  { color: '#64748b', emoji: '♻️' },
  perf:       { color: '#ca8a04', emoji: '⏱️' },
  error:      { color: '#dc2626', emoji: '❌' },
  i18n:       { color: '#16a34a', emoji: '🌐' },
  form:       { color: '#0369a1', emoji: '📝' },
};

const REDACT_KEY_PATTERNS = [
  /^password$/i, /token/i, /secret/i, /apikey/i, /^authorization$/i,
  /clientsecret/i, /^pwd$/i, /^pass$/i,
];

const PII_KEY_PATTERNS = [
  /^email$/i, /^mail$/i, /^firstname$/i, /^lastname$/i, /^name$/i, /^fullname$/i,
];

function maskEmail(s: string): string {
  if (!s.includes('@')) return s;
  const [local, domain] = s.split('@');
  if (!local || !domain) return s;
  if (local.length <= 2) return `${local[0]}***@${domain}`;
  return `${local[0]}***${local[local.length - 1]}@${domain}`;
}

function maskName(s: string): string {
  if (s.length <= 2) return s;
  return `${s[0]}*** (${s.length})`;
}

function sanitize(value: unknown, mode: LogVerbosity): unknown {
  if (value == null) return value;
  if (typeof value === 'string') return value;
  if (typeof value !== 'object') return value;

  if (Array.isArray(value)) return value.map((v) => sanitize(v, mode));

  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (REDACT_KEY_PATTERNS.some((p) => p.test(k))) {
      out[k] = '[REDACTED]';
      continue;
    }
    if (mode !== 'dev' && PII_KEY_PATTERNS.some((p) => p.test(k))) {
      if (typeof v === 'string') {
        out[k] = /@/.test(v) ? maskEmail(v) : maskName(v);
      } else {
        out[k] = v;
      }
      continue;
    }
    out[k] = sanitize(v, mode);
  }
  return out;
}

const BUFFER_MAX = 500;
const buffer: LogEntry[] = [];
const startedAt = performance.now();

let currentVerbosity: LogVerbosity = (() => {
  try {
    const stored = localStorage.getItem('pm:logVerbosity') as LogVerbosity | null;
    if (stored && stored in VERBOSITY_THRESHOLD) return stored;
  } catch { /* SSR / privacy mode */ }
  // PM-Frontend ist aktuell DEV-Tool — Default auf 'dev' (alles inkl. trace/debug).
  // Override via localStorage.setItem('pm:logVerbosity', 'normal'|'tester'|'silent').
  return 'dev';
})();

function shouldLog(level: LogLevel): boolean {
  return LEVEL_RANK[level] >= VERBOSITY_THRESHOLD[currentVerbosity];
}

function appendBuffer(entry: LogEntry): void {
  buffer.push(entry);
  if (buffer.length > BUFFER_MAX) buffer.shift();
}

function emitConsole(entry: LogEntry): void {
  const style = CATEGORY_STYLE[entry.category];
  const tag = `${style.emoji} [${entry.category.toUpperCase()}]`;
  const corr = entry.correlationId ? ` corr:${entry.correlationId}` : '';
  const prefix = `%c${tag}%c ${entry.relMs.toFixed(0)}ms${corr}`;
  const tagStyle = `color: ${style.color}; font-weight: bold;`;
  const restStyle = 'color: inherit; font-weight: normal;';

  const args: unknown[] = [prefix, tagStyle, restStyle, entry.message];
  if (entry.data !== undefined) args.push(entry.data);

  switch (entry.level) {
    case 'trace':
    case 'debug':
      console.debug(...args);
      break;
    case 'info':
      console.info(...args);
      break;
    case 'warn':
      console.warn(...args);
      break;
    case 'error':
      console.error(...args);
      break;
  }
}

function log(
  level: LogLevel,
  category: LogCategory,
  message: string,
  data?: Record<string, unknown>,
  correlationId?: string,
): void {
  if (!shouldLog(level)) return;
  const sanitizedData = data ? (sanitize(data, currentVerbosity) as Record<string, unknown>) : undefined;
  const entry: LogEntry = {
    ts: new Date().toISOString(),
    relMs: Math.round(performance.now() - startedAt),
    level,
    category,
    message,
    data: sanitizedData,
    correlationId,
  };
  appendBuffer(entry);
  emitConsole(entry);
}

export const logger = {
  trace: (cat: LogCategory, msg: string, data?: Record<string, unknown>, corr?: string) => log('trace', cat, msg, data, corr),
  debug: (cat: LogCategory, msg: string, data?: Record<string, unknown>, corr?: string) => log('debug', cat, msg, data, corr),
  info:  (cat: LogCategory, msg: string, data?: Record<string, unknown>, corr?: string) => log('info',  cat, msg, data, corr),
  warn:  (cat: LogCategory, msg: string, data?: Record<string, unknown>, corr?: string) => log('warn',  cat, msg, data, corr),
  error: (cat: LogCategory, msg: string, data?: Record<string, unknown>, corr?: string) => log('error', cat, msg, data, corr),

  group: (label: string, fn: () => void) => {
    if (currentVerbosity === 'silent') return;
    console.groupCollapsed(`%c${label}`, 'font-weight: bold; color: #475569;');
    try { fn(); } finally { console.groupEnd(); }
  },

  setVerbosity: (v: LogVerbosity) => {
    currentVerbosity = v;
    try { localStorage.setItem('pm:logVerbosity', v); } catch { /* ignore */ }
    log('info', 'lifecycle', `Logger verbosity = ${v}`);
  },

  getVerbosity: (): LogVerbosity => currentVerbosity,

  getBuffer: (): LogEntry[] => [...buffer],

  formatBufferAsText: (): string => {
    return buffer
      .map((e) => {
        const corr = e.correlationId ? ` corr:${e.correlationId}` : '';
        const data = e.data !== undefined ? ` ${JSON.stringify(e.data)}` : '';
        return `${e.ts} +${e.relMs}ms [${e.level.toUpperCase()}] [${e.category}]${corr} ${e.message}${data}`;
      })
      .join('\n');
  },

  clearBuffer: (): void => {
    buffer.length = 0;
  },
};

export type Logger = typeof logger;
